import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { ControlledInput, IParams, Page, Section, Widget } from '../../../shared';
import { ISaveFoodRestriction } from '../../models';
import { useFoodRestriction, useSaveFoodRestriction } from '../../hooks/food-restriction.hook';

export const FoodRestrictionEditPage: FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>() as IParams;
    const navigate = useNavigate();

    const { data: foodRestriction, isFetching } = useFoodRestriction(id);
    const { mutateAsync: saveFoodRestriction, isPending } = useSaveFoodRestriction();

    const form = useForm<ISaveFoodRestriction>({
        resolver: yupResolver(object().shape({ name: string().required() })),
        mode: 'onSubmit',
    });

    useEffect(() => {
        if (foodRestriction) form.reset(foodRestriction);
    }, [form, foodRestriction]);

    const onSubmit = useCallback(
        async (data: ISaveFoodRestriction) => {
            await saveFoodRestriction({ data });
            navigate('/admin/food-restrictions');
        },
        [saveFoodRestriction, navigate],
    );

    const actions = useMemo(
        () => [
            <Button onClick={() => navigate('/admin/food-restrictions')} color="secondary">
                {t('cancel')}
            </Button>,
            <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                {t('save')}
            </Button>,
        ],
        [form, onSubmit, isPending, t, navigate],
    );

    return (
        <Page
            title={<Box width="300px">{id ? t('editFoodRestriction') : t('newFoodRestriction')}</Box>}
            actions={actions}
            onBack={() => navigate(-1)}
            loading={isFetching || isPending}
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Widget footer={[...actions].reverse()}>
                        <Section title={t('data')}>
                            <ControlledInput name="name" label={t('nameLabel')} required sx={{ width: '400px' }} />
                        </Section>
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
};
